import React, { useState, useEffect } from "react";
import { X, Minus, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";

export default function ProductDetailModal({ isOpen, onClose, product, onAddToCart }) {
    const [qty, setQty] = useState(1);
    const [notes, setNotes] = useState("");
    const hasVariants = product?.variants && product.variants.length > 0;
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [isGift, setIsGift] = useState(false);

    useEffect(() => {
        if (isOpen && product) {
            const initialQty = product.product_type === 'weight_based' ? 0.25 : 1;
            // Default to 1 (or 0.25) but cap it at available stock if stock is low
            const stockVal = parseFloat(product.variants && product.variants.length > 0 ? product.variants[0].stock : product.stock) || 0;
            setQty(stockVal > 0 ? Math.min(initialQty, stockVal) : initialQty);
            setNotes("");
            setSelectedVariant(product.variants && product.variants.length > 0 ? product.variants[0] : null);
            setIsGift(false);
        }
    }, [isOpen, product]);

    if (!isOpen || !product) return null;

    const currentPrice = selectedVariant ? Number(selectedVariant.unit_price) : product.price;
    const currentUnit = selectedVariant ? selectedVariant.name : (product.unit || 'unit');

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white dark:bg-gray-800 w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative border border-gray-200 dark:border-gray-700"
                >
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-gray-100 dark:bg-gray-700/50 hover:bg-black/5 rounded-full z-20"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>

                    {/* Left: Image (45%) */}
                    <div className="w-full md:w-5/12 bg-gray-100 dark:bg-gray-900/60 flex items-center justify-center p-8 min-h-[350px] md:min-h-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-800">
                        <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-inner border border-gray-100 dark:border-gray-700">
                             {product.image ? (
                                <img 
                                    src={resolveMediaUrl(product.image)} 
                                    alt={product.name} 
                                    className="w-full h-full object-cover" 
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://placehold.co/400x400?text=Product+Image';
                                    }}
                                />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center gap-4 opacity-40">
                                    <span className="text-8xl">🥘</span>
                                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">No Image Available</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Content (55%) - Scrollable */}
                    <div className="w-full md:w-7/12 flex flex-col max-h-[90vh]">
                        <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar">
                            <div className="mb-6">
                                <h2 className="text-3xl font-black text-gray-900 dark:text-white leading-tight">{product.name}</h2>
                                {product.asn_code && (
                                    <p className="text-xs text-gray-500 mt-2 font-bold uppercase tracking-widest">
                                        ASN: <span className="text-gray-900 dark:text-gray-300">{product.asn_code}</span>
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col mb-8">
                                <span className="text-4xl font-black text-green-600 tracking-tighter">₹{currentPrice.toFixed(2)}</span>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 bg-gray-50 dark:bg-gray-700/30 px-2 py-0.5 rounded w-fit">
                                    {product.product_type === 'weight_based' ? 'Price per kg' : (hasVariants ? selectedVariant?.name : `per ${currentUnit}`)}
                                </span>
                            </div>

                            {/* Variants - Swiggy/Zomato Style */}
                            {hasVariants && (
                                <div className="mb-6 bg-gray-50/50 dark:bg-gray-900/40 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                                    <label className="block text-xs font-bold uppercase text-gray-400 mb-4 tracking-wider">Select Option</label>
                                    <div className="flex flex-col gap-2.5">
                                        {product.variants.map((v) => (
                                            <button
                                                key={v.id}
                                                onClick={() => setSelectedVariant(v)}
                                                className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all group ${
                                                    selectedVariant?.id === v.id 
                                                    ? 'border-green-600 bg-green-50/50 dark:bg-green-900/20' 
                                                    : 'border-transparent bg-white dark:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700 shadow-sm'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                        selectedVariant?.id === v.id ? 'border-green-600' : 'border-gray-300'
                                                    }`}>
                                                        {selectedVariant?.id === v.id && (
                                                            <div className="w-2.5 h-2.5 rounded-full bg-green-600" />
                                                        )}
                                                    </div>
                                                    <span className={`text-sm font-bold ${selectedVariant?.id === v.id ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900'}`}>
                                                        {v.name}
                                                    </span>
                                                </div>
                                                <span className={`text-sm font-bold ${selectedVariant?.id === v.id ? 'text-green-700 dark:text-green-400' : 'text-gray-500'}`}>
                                                    ₹{Number(v.unit_price).toFixed(2)}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Note Input */}
                            <div className="mb-8">
                                <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Special Instructions?</label>
                                <textarea
                                    className="w-full bg-gray-50 dark:bg-gray-900/20 rounded-xl p-4 text-sm dark:text-white outline-none ring-1 ring-gray-100 dark:ring-gray-700 focus:ring-2 focus:ring-green-100 dark:focus:ring-green-900/20 resize-none transition-all"
                                    rows="2"
                                    placeholder="e.g. Less spicy, Extra cheese..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>

                            {/* Gift Product Option */}
                            <div className="mb-6 p-4 rounded-2xl border-2 border-dashed border-purple-200 bg-purple-50/30 dark:border-purple-900/30 dark:bg-purple-900/10 flex items-center justify-between group hover:border-purple-400 transition-all cursor-pointer" onClick={() => setIsGift(!isGift)}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm transition-all ${isGift ? 'bg-purple-600' : 'bg-white dark:bg-gray-800'}`}>
                                        {isGift ? '🎁' : '🛍️'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Gift this product?</p>
                                        <p className="text-[10px] font-bold text-purple-600/70 uppercase">Price will become ₹0.00</p>
                                    </div>
                                </div>
                                <div className={`w-12 h-6 rounded-full p-1 transition-all ${isGift ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-all ${isGift ? 'translate-x-6' : 'translate-x-0'}`} />
                                </div>
                            </div>

                            {/* Quantity Selector Inline */}
                            <div className="flex flex-col gap-4 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-700 mb-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">
                                        {product.product_type === 'weight_based' ? 'Weight (KG)' : 'Quantity'}
                                    </span>
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => setQty(Math.max(product.product_type === 'weight_based' ? 0.05 : 1, parseFloat(qty || 0) - (product.product_type === 'weight_based' ? 0.05 : 1)))}
                                            className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                                        >
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <div className="flex flex-col items-center">
                                            <input
                                                type="number"
                                                step={product.product_type === 'weight_based' ? "0.01" : "1"}
                                                value={qty}
                                                onChange={(e) => setQty(e.target.value)}
                                                placeholder="0.00"
                                                className="text-2xl font-black text-gray-900 dark:text-white w-24 text-center bg-transparent outline-none"
                                            />
                                        </div>
                                        <button
                                            onClick={() => setQty(parseFloat(qty || 0) + (product.product_type === 'weight_based' ? 0.05 : 1))}
                                            className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {product.product_type === 'weight_based' && (
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">In Grams (GM)</span>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="number"
                                                value={Math.round(parseFloat(qty || 0) * 1000)}
                                                onChange={(e) => {
                                                    const g = parseFloat(e.target.value || 0);
                                                    setQty(g / 1000);
                                                }}
                                                className="text-xl font-bold text-blue-600 dark:text-blue-400 w-24 text-right bg-white dark:bg-gray-800 px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-700 outline-none focus:ring-2 focus:ring-blue-100"
                                            />
                                            <span className="text-xs font-bold text-gray-400 uppercase">gm</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                            <button
                                onClick={() => {
                                    onAddToCart({ 
                                        ...product, 
                                        qty, 
                                        notes,
                                        price: isGift ? 0 : currentPrice,
                                        original_price: currentPrice,
                                        is_gift: isGift,
                                        variant_id: selectedVariant?.id,
                                        variant_name: selectedVariant?.name,
                                        cartItemId: selectedVariant ? `${product.id}-${selectedVariant.id}${isGift ? '-gift' : ''}` : `${product.id}${isGift ? '-gift' : ''}`,
                                        stock: selectedVariant ? selectedVariant.stock : product.stock 
                                    });
                                    onClose();
                                }}
                                className="w-full py-5 bg-green-600 text-white rounded-2xl font-black text-lg hover:bg-green-700 active:scale-[0.98] transition-all shadow-xl shadow-green-500/20 flex items-center justify-center gap-3"
                            >
                                Add to Cart (₹{(currentPrice * qty).toFixed(2)})
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
